import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, EnvelopeIcon, MapPinIcon, ClockIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';

export default function ContactPage() {
  // Function to open the Crisp chat widget if available
  const openChat = () => {
    if (window.$crisp) {
      window.$crisp.push(['do', 'chat:open']);
    } else {
      alert('Chat is currently unavailable. Please email us instead.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-pri-dark to-sec-dark text-white">
      {/* Header with navigation back */}
      <header className="bg-pri-dark/90 backdrop-blur-md fixed top-0 w-full z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img
              className="h-10 w-auto"
              src={`${process.env.REACT_APP_CDN_URL}/logo/logo-no-bg.png`}
              alt="Jingleez"
            />
          </Link>
          
          <Link
            to="/"
            className="inline-flex items-center text-sm font-semibold rounded-full px-4 py-2 bg-white/10 hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-soft-pink">Contact Us</h1>
            <div className="h-1 w-24 bg-mint-green mx-auto mb-6"></div>
            <p className="text-pri-gray text-lg max-w-2xl mx-auto">
              We'd love to hear from you! Reach out with any questions, feedback, or suggestions.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-gradient-to-br from-pri-dark/80 to-sec-dark/90 rounded-2xl p-8 border border-white/10 shadow-lg"
            >
              <h2 className="text-2xl font-bold mb-6 text-mint-green">Get in Touch</h2>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="h-10 w-10 rounded-full bg-mint-green/10 flex items-center justify-center mr-4 mt-1">
                    <EnvelopeIcon className="h-5 w-5 text-mint-green" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-white mb-1">Email Us</h3>
                    <p className="text-pri-gray">
                      <a href="mailto:contact@jingleez.com" className="text-mint-green hover:text-mint-green/80 transition-colors">
                        contact@jingleez.com
                      </a>
                    </p>
                    <p className="text-pri-gray text-sm mt-1">
                      We typically respond within 24 hours on business days.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="h-10 w-10 rounded-full bg-mint-green/10 flex items-center justify-center mr-4 mt-1">
                    <MapPinIcon className="h-5 w-5 text-mint-green" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-white mb-1">Our Location</h3>
                    <p className="text-pri-gray">
                      Newbury, Berkshire, UK
                    </p>
                    <p className="text-pri-gray text-sm mt-1">
                      Currently operating remotely
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="h-10 w-10 rounded-full bg-mint-green/10 flex items-center justify-center mr-4 mt-1">
                    <ClockIcon className="h-5 w-5 text-mint-green" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-white mb-1">Support Hours</h3>
                    <p className="text-pri-gray">
                      Monday - Friday: 9am - 5pm PST
                    </p>
                    <p className="text-pri-gray text-sm mt-1">
                      Weekend support available for urgent matters
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 p-6 bg-white/5 rounded-xl border border-white/10">
                <p className="text-pri-gray text-sm">
                  As a beta platform, we're especially eager to hear your feedback and suggestions. Your input helps shape the future of Jingleez!
                </p>
              </div>
            </motion.div>
            
            {/* Live Chat Support */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="flex flex-col"
            >
              <div className="bg-gradient-to-br from-pri-dark/80 to-sec-dark/90 rounded-2xl p-8 border border-white/10 shadow-lg h-full flex flex-col">
                <h2 className="text-2xl font-bold mb-6 text-soft-pink">Live Chat Support</h2>
                
                <div className="flex-1 flex flex-col items-center justify-center text-center p-6">
                  <div className="h-24 w-24 rounded-full bg-soft-pink/10 flex items-center justify-center mb-6 animate-pulse">
                    <ChatBubbleLeftRightIcon className="h-12 w-12 text-soft-pink" />
                  </div>
                  
                  <h3 className="text-xl font-bold mb-4">Need Immediate Assistance?</h3>
                  <p className="text-pri-gray mb-8">
                    Our support team is available to chat during business hours. Click below to start a conversation!
                  </p>
                  
                  <motion.button
                    onClick={openChat}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-full py-4 bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark font-bold rounded-xl flex items-center justify-center"
                  >
                    <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
                    Start Live Chat
                  </motion.button>
                  
                  <p className="text-pri-gray text-sm mt-4">
                    If we're offline, we'll respond to your message as soon as we're back.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 mt-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-pri-gray">© {new Date().getFullYear()} Jingleez. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
} 